import { LinkOutlined, UserOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { App } from 'antd';
import React from 'react';
import Exception403 from './pages/exception/403';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  SelectLang,
} from '@/components';
import { getCurrentUserAuthz } from '@/services/admin/rbac';
import { getCurrentAdminUser } from '@/services/admin/user';
import { getToken } from '@/utils/auth';
import { normalizeAuthz } from '@/utils/authz';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.AdminUserResponse;
  /** 当前用户角色与权限（含 system_admin 前端兜底全量权限） */
  authz?: API.AdminUserAuthzResponse;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.AdminUserResponse | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const token = getToken();
      if (!token) return undefined;

      const response = await getCurrentAdminUser({
        skipErrorHandler: true,
      });
      if (response.code === 0 && response.data) {
        return response.data;
      }
      return undefined;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    let authz: API.AdminUserAuthzResponse | undefined;
    if (currentUser) {
      try {
        const res = await getCurrentUserAuthz({ skipErrorHandler: true });
        if (res.code === 0 && res.data) {
          authz = normalizeAuthz(res.data);
        }
      } catch {
        // 忽略 authz 失败，仅无权限能力
      }
    }
    return {
      fetchUserInfo,
      currentUser,
      authz,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar_url,
      icon: !initialState?.currentUser?.avatar_url ? <UserOutlined /> : undefined,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.nickname || initialState?.currentUser?.username,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    /** 无权限访问路由时展示 403 页（非登录用户由 onPageChange 重定向登录） */
    unAccessible: <Exception403 />,
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <App>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </App>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 根据环境变量自动获取对应的 baseURL
  // 本地开发及构建环境均使用 env/ 目录中对应环境的配置
  // 注意：使用 process.env.API_BASE_URL（在构建时通过 define 注入）
  // 配置来源：env/ 目录下的配置文件（env/dev.ts, env/test.ts, env/prod.ts）
  baseURL: (() => {
    if (typeof process === 'undefined' || !process.env) return undefined;
    let raw = process.env.API_BASE_URL as string | undefined;
    if (raw == null || raw === '' || raw === 'undefined') return undefined;
    raw = String(raw).trim();
    // 去掉外层引号（应对 define 被重复 JSON 序列化等异常情况）
    for (let i = 0; i < 3; i += 1) {
      const dq = raw.startsWith('"') && raw.endsWith('"');
      const sq = raw.startsWith("'") && raw.endsWith("'");
      if (dq || sq) raw = raw.slice(1, -1).trim();
      else break;
    }
    return raw || undefined;
  })(),
  ...errorConfig,
};
